import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Paper,
    Title,
    Text,
    PasswordInput,
    Button,
    Stack,
    Alert,
    Anchor,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { authApi } from '../api/auth.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { ApiError } from '../api/client.js';

const MIN_LEN = 8;

export default function ChangePasswordPage() {
    const { user, refresh, logout } = useAuth();
    const navigate = useNavigate();
    const isForced = !!user?.mustChangePassword;

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setError(null);

        if (newPassword.length < MIN_LEN) {
            setError(`Минимальная длина пароля — ${MIN_LEN} символов.`);
            return;
        }
        if (newPassword !== confirm) {
            setError('Пароли не совпадают.');
            return;
        }

        setSubmitting(true);
        try {
            await authApi.changePassword(
                isForced ? undefined : currentPassword,
                newPassword,
            );
            notifications.show({
                color: 'green',
                title: 'Пароль изменён',
                message: 'Используйте новый пароль для следующего входа.',
            });
            // Перечитываем /me — must_change_password снимется, контекст обновится.
            // ProtectedRoute сам разрулит дальше (если ПДн ещё не принято — на /accept-pdn).
            await refresh();
            navigate('/', { replace: true });
        } catch (err) {
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError('Не удалось сменить пароль. Попробуйте позже.');
            }
            setSubmitting(false);
        }
    };

    const handleCancel = async () => {
        // На принудительной смене «отмена» = выйти и зайти снова под другим логином
        await logout();
        navigate('/login', { replace: true });
    };

    return (
        <Paper p="xl" radius="md" withBorder shadow="sm">
            <Stack gap="md">
                <div>
                    <Title order={2}>Смена пароля</Title>
                    <Text size="sm" c="dimmed" mt={4}>
                        {isForced
                            ? 'При первом входе нужно сменить временный пароль на свой.'
                            : `Минимум ${MIN_LEN} символов. Новый пароль не должен совпадать с текущим.`}
                    </Text>
                </div>

                <form onSubmit={onSubmit} autoComplete="off" noValidate>
                    <Stack gap="md">
                        {!isForced && (
                            <PasswordInput
                                label="Текущий пароль"
                                required
                                autoComplete="current-password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.currentTarget.value)}
                                disabled={submitting}
                            />
                        )}
                        <PasswordInput
                            label="Новый пароль"
                            required
                            minLength={MIN_LEN}
                            autoComplete="new-password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.currentTarget.value)}
                            disabled={submitting}
                        />
                        <PasswordInput
                            label="Повторите пароль"
                            required
                            minLength={MIN_LEN}
                            autoComplete="new-password"
                            value={confirm}
                            onChange={(e) => setConfirm(e.currentTarget.value)}
                            disabled={submitting}
                        />

                        {error && (
                            <Alert color="red" variant="light" role="alert">
                                {error}
                            </Alert>
                        )}

                        <Button type="submit" loading={submitting} fullWidth size="md">
                            Сохранить
                        </Button>

                        {isForced && (
                            <Text size="xs" c="dimmed" ta="center">
                                <Anchor component="button" type="button" onClick={handleCancel}>
                                    Войти под другим аккаунтом
                                </Anchor>
                            </Text>
                        )}
                    </Stack>
                </form>
            </Stack>
        </Paper>
    );
}
